import { LoadingOutlined } from "@ant-design/icons";
import { useUserActions } from "_actions";
import { UserProfileType } from "_interfaces";
import { statesAndUTsOfIndia, titleCase } from "_lib/utils";
import { Button, Form, Input, message, Select, Spin } from "antd";
import { useForm } from "antd/es/form/Form";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export const ProfilePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [generalForm] = useForm();
  const [passwordForm] = useForm();
  const [profile, setProfile] = useState<UserProfileType>();
  const userActions = useUserActions();
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    userActions.getProfile().then((response) => {
      if (response.error) {
        message.error("Failed to fetch profile");
      } else {
        setProfile(response.data);
        generalForm.setFieldsValue(response.data);
      }
      setIsLoading(false);
    });
  }, []);

  const onGeneralFormFinish = (values: any) => {
    delete values.email;
    delete values.password;
    userActions.update(values as UserProfileType).then((response) => {
      if (response.error) {
        message.error("Failed to update profile");
      } else {
        message.success("Profile updated successfully");
        navigate("/");
      }
    });
  };

  const onPasswordFormFinish = (values: {
    oldPassword: string;
    newPassword: string;
  }) => {
    userActions
      .changePassword(values.oldPassword, values.newPassword)
      .then((response) => {
        if (response.error) {
          message.error("Failed to update password");
        } else {
          message.success("Password updated successfully");
          passwordForm.resetFields();
        }
      });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spin indicator={<LoadingOutlined spin />} size="large" />
      </div>
    );
  }
  return (
    <section className="mx-auto max-w-4xl space-y-5">
      <div className="bg-white rounded-lg px-8 pt-8">
        <h1 className="text-lg font-semibold pb-4">General Information</h1>
        <Form
          form={generalForm}
          className="py-4"
          layout="horizontal"
          validateTrigger="onBlur"
          wrapperCol={{ span: 17 }}
          labelCol={{ span: 5 }}
          initialValues={profile}
          onFinish={onGeneralFormFinish}
        >
          <Form.Item
            name={"firstName"}
            label="First Name"
            normalize={(value) => titleCase(value)}
            rules={[
              {
                required: true,
                message: "First Name is required",
                type: "string",
              },
            ]}
          >
            <Input placeholder="First Name" />
          </Form.Item>
          <Form.Item
            name={"lastName"}
            label="Last Name"
            normalize={(value) => titleCase(value)}
            rules={[
              {
                required: true,
                message: "Last Name is required",
                type: "string",
              },
            ]}
          >
            <Input placeholder="Last Name" />
          </Form.Item>
          <Form.Item name={"email"} label="Email Address">
            <Input placeholder="Email Address" readOnly disabled />
          </Form.Item>
          <Form.Item
            name={"mobile"}
            label="Mobile Number"
            normalize={(value) => value.replace(/\D/g, "")}
            rules={[
              {
                required: true,
                message: "Mobile Number is required",
              },
              {
                pattern: /^[0-9]+$/,
                message: "Mobile Number should contain only digits",
              },
              {
                len: 10,
                message: "Mobile Number should be 10 digits",
              },
            ]}
          >
            <Input placeholder="Mobile Number" prefix="+91 - " />
          </Form.Item>
          <Form.Item
            name={"address1"}
            label="Address Line 1"
            rules={[
              {
                required: true,
                message: "Address line 1 is required",
                type: "string",
              },
              {
                max: 100,
                message: "Address should be less than 100 characters",
              },
            ]}
          >
            <Input placeholder="Address Line 1" />
          </Form.Item>
          <Form.Item
            name={"address2"}
            label="Address Line 2"
            rules={[
              {
                required: false,
                type: "string",
              },
              {
                max: 100,
                message: "Address should be less than 100 characters",
              },
            ]}
          >
            <Input placeholder="Address Line 2" />
          </Form.Item>
          <Form.Item
            name={"city"}
            label="City"
            normalize={(value) => titleCase(value)}
            rules={[
              {
                required: true,
                message: "City is required",
                type: "string",
              },
              {
                max: 50,
                message: "City should be less than 50 characters",
              },
              {
                pattern: /^[a-zA-Z\s]+$/,
                message: "City should contain only alphabets",
              },
            ]}
          >
            <Input placeholder="City" />
          </Form.Item>
          <Form.Item
            name={"state"}
            label="State"
            rules={[
              {
                required: true,
                message: "State is required",
                type: "string",
              },
            ]}
          >
            <Select placeholder="Select State or Union Territory">
              {statesAndUTsOfIndia.map((stateOrUT) => (
                <Select.Option key={stateOrUT} value={stateOrUT}>
                  {stateOrUT}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={"company"}
            label="Company"
            normalize={(value) => titleCase(value)}
            rules={[
              {
                required: true,
                message: "Company is required",
                type: "string",
              },
            ]}
          >
            <Input placeholder="Company" />
          </Form.Item>
          <Form.Item
            name={"designation"}
            label="Designation"
            normalize={(value) => titleCase(value)}
            rules={[
              {
                required: true,
                message: "Designation is required",
                type: "string",
              },
            ]}
          >
            <Input placeholder="Designation" />
          </Form.Item>
          <Form.Item
            name={"pan"}
            label="PAN"
            normalize={(value) => value.toUpperCase()}
            rules={[
              {
                required: true,
                message: "PAN is required",
                type: "string",
              },
              {
                pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                message: "Invalid PAN format",
              },
              {
                max: 10,
              },
            ]}
          >
            <Input placeholder="PAN" maxLength={10} />
          </Form.Item>
          <Form.Item
            name={"aadhaar"}
            label="Aadhaar Number"
            rules={[
              {
                required: true,
                message: "Aadhaar Number is required",
                type: "string",
              },
              {
                pattern: /^[0-9]{12}$/,
                message: "Aadhaar Number should be 12 digits",
              },
              {
                max: 12,
              },
            ]}
          >
            <Input placeholder="Aadhaar Number" max={12} maxLength={12} />
          </Form.Item>
          <Form.Item className="flex justify-end">
            <Button type="primary" htmlType="submit">
              Update Profile
            </Button>
          </Form.Item>
        </Form>
      </div>

      <div className="bg-white rounded-lg px-8 pt-8">
        <h1 className="text-lg font-semibold pb-4">Update Password</h1>
        <Form
          form={passwordForm}
          className="lg:grid grid-cols-1 lg:grid-cols-2 gap-y-2 gap-x-20 items-center py-4"
          layout="vertical"
          requiredMark={false}
          validateTrigger="onBlur"
          onFinish={onPasswordFormFinish}
        >
          <Form.Item
            name={"oldPassword"}
            label="Current Password"
            rules={[
              {
                required: true,
                message: "Current Password is required",
              },
            ]}
          >
            <Input.Password placeholder="Current Password" />
          </Form.Item>
          <Form.Item
            name={"newPassword"}
            label="New Password"
            rules={[
              {
                required: true,
                message: "Please input your password!",
                type: "string",
              },
              {
                min: 6,
                message: "Password must be at least 6 characters long",
              },
              {
                max: 20,
                message: "Password must be at most 20 characters long",
              },
              {
                pattern:
                  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,20}$/,
                message:
                  "Password must contain at least one letter, one number and one special character (@$!%*#?&)",
              },
            ]}
          >
            <Input.Password placeholder="New Password" />
          </Form.Item>
          <Form.Item className="col-span-3 text-end">
            <Button type="primary" htmlType="submit">
              Update Password
            </Button>
          </Form.Item>
        </Form>
      </div>
    </section>
  );
};
